// The Eventbrite card's words and pure helpers (Events design B, step 1,
// 2026-10-19). Pure so they test without a server.
//
// 🔴 THE TOKEN NEVER LIVES IN REACT STATE. The field is uncontrolled; the
// token is read from it at the moment of Connect, travels once in the PUT
// body, and the field is emptied when the server says it is stored. Nothing
// here keeps it, logs it, or puts it in a URL or in storage.

export const EVENTBRITE_KEYS_URL = "https://www.eventbrite.com/platform/api-keys";

// The server refuses anything longer (backend PUT /admin/integrations/eventbrite).
export const TOKEN_MAX = 200;

export const COPY = {
  intro:
    "Paste your Eventbrite Private token. Find it on eventbrite.com under Account settings, Developer links, API keys.",
  confirmDisconnect:
    "Disconnect Eventbrite? Curiouzz will stop checking registrations until you connect again.",
  empty: "Paste the Private token first.",
  spaces:
    "That has a space or a line break inside it. Copy the Private token again from Eventbrite and paste only that.",
  tooLong:
    "That is longer than an Eventbrite key. Copy the Private token again from Eventbrite and paste only that.",
  // The server's own words for these arrive in its answer and win; these are
  // only for an answer that carries none.
  badKey:
    "Eventbrite did not accept that key. Copy the Private token again from Eventbrite and paste it here.",
  unreachable: "Couldn't reach Eventbrite. Try again in a minute.",
  notDeployed:
    "This server does not have the Eventbrite connection yet. The backend needs to be deployed first.",
  // A timeout or a 5xx may still have landed, so never claim it did not.
  unconfirmed:
    "The server did not confirm this. Reload the page to see whether Eventbrite is connected before trying again.",
  loadFailed: "Couldn't load the Eventbrite connection. Reload the page to try again.",
  // Only a Super Admin may connect or disconnect (the server refuses everyone
  // else and says so in its own words).
  viewOnly: "Not connected. A Super Admin can connect it.",
};

/**
 * Can this browser hide what is typed in a plain text field (CSS
 * -webkit-text-security)? The token field is a masked text field rather than
 * a password field where it can be, so the browser never offers to save the
 * token as a password or fills a saved sign-in password into it (browsers
 * ignore autocomplete="off" on password fields). Where it cannot, the field
 * falls back to a password field marked new-password, which at least keeps
 * saved sign-ins out of it.
 */
export const textMaskSupported = () => {
  try {
    return (
      typeof window !== "undefined" &&
      !!window.CSS &&
      typeof window.CSS.supports === "function" &&
      window.CSS.supports("-webkit-text-security", "disc")
    );
  } catch (e) {
    return false;
  }
};

const text = (v) => (typeof v === "string" && v.trim() ? v.trim() : null);

/**
 * The connection as the card shows it, from the server's answer whether bare
 * or wrapped in { data }. Only the six named fields are kept: if a server
 * bug ever sent the token along in another field, it would not reach state.
 * The key ending is cut to four characters for the same reason.
 * canChange: may this admin connect or disconnect (the server says false for
 * anyone but a Super Admin). An answer without it counts as yes, since the
 * server refuses anyone it should anyway.
 */
export const normaliseStatus = (body) => {
  const inner =
    body && typeof body === "object" && body.data && typeof body.data === "object" && "connected" in body.data
      ? body.data
      : body;
  if (!inner || typeof inner !== "object") {
    return { connected: false, organizationName: null, userEmail: null, tokenLast4: null, connectedAt: null, canChange: true };
  }
  const last4 = text(inner.tokenLast4);
  return {
    connected: inner.connected === true,
    organizationName: text(inner.organizationName),
    userEmail: text(inner.userEmail),
    tokenLast4: last4 ? last4.slice(-4) : null,
    connectedAt: inner.connectedAt || null,
    canChange: inner.canChange !== false,
  };
};

export const NOT_CONNECTED = normaliseStatus({ connected: false });

/** What the field holds, checked the way the server checks it. */
export const checkToken = (raw) => {
  const token = String(raw == null ? "" : raw).trim();
  if (!token) return { error: COPY.empty };
  if (/\s/.test(token)) return { error: COPY.spaces };
  if (token.length > TOKEN_MAX) return { error: COPY.tooLong };
  return { token };
};

/** "Oct 19, 2026", on Ottawa's calendar whatever clock this computer keeps. */
export const formatSince = (value) => {
  if (!value) return null;
  const at = new Date(value);
  if (!Number.isFinite(at.getTime())) return null;
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Toronto",
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(at);
};

/** "Connected to {organizationName} ({userEmail}), key ending {tokenLast4}, since {date}" */
export const connectedLine = (status) => {
  const s = status || {};
  const who = s.organizationName ? `Connected to ${s.organizationName}` : "Connected to Eventbrite";
  const parts = [s.userEmail ? `${who} (${s.userEmail})` : who];
  if (s.tokenLast4) parts.push(`key ending ${s.tokenLast4}`);
  const since = formatSince(s.connectedAt);
  if (since) parts.push(`since ${since}`);
  return parts.join(", ");
};

/** The closed drawer's line, from the live answer (never hand-written). */
export const integrationsSummary = (state) => {
  if (!state || state.loading) return "Eventbrite: checking";
  if (state.loadError) return "Eventbrite: status unknown";
  const s = state.status || {};
  if (!s.connected) return "Eventbrite not connected";
  return s.organizationName ? `Eventbrite connected to ${s.organizationName}` : "Eventbrite connected";
};

/** What to say when a Connect or Disconnect did not go through. */
export const errorOf = (err, action) => {
  const res = err && err.response;
  const status = res && res.status;
  // The endpoint has no 404 of its own, so a 404 is a server without it.
  if (status === 404) return COPY.notDeployed;
  const message = res && res.data && typeof res.data === "object" ? text(res.data.message) : null;
  if (message) return message;
  if (action === "connect" && status === 400) return COPY.badKey;
  if (status === 502) return COPY.unreachable;
  if (status && status < 500) return `The server refused this (HTTP ${status}). Nothing was changed.`;
  return COPY.unconfirmed;
};

/** What to say when the card could not read the connection at all. */
export const loadErrorOf = (err) => {
  const status = err && err.response && err.response.status;
  return status === 404 ? COPY.notDeployed : COPY.loadFailed;
};
